import React from 'react';
import { ExternalLink, AlertTriangle, HelpCircle, History, Settings } from 'lucide-react';
import { Input } from '../ui/Input';
import { MarkdownEditor } from './MarkdownEditor';

interface ChangelogEntry {
  version: string;
  date: string;
  changes: string[];
}

interface FaqEntry {
  question: string;
  answer: string;
}

interface EnvironmentRequirement {
  environment: 'sandbox' | 'production';
  requirements: string[];
  notes?: string;
}

interface DocumentationTabProps {
  templateId?: string;
  description: string;
  documentation: string;
  documentationUrl: string;
  setupGuide: string;
  changelog: ChangelogEntry[];
  faq: FaqEntry[];
  knownLimitations: string[];
  environmentRequirements: EnvironmentRequirement[];
  onChange: (field: string, value: any) => void;
  isDarkMode?: boolean;
}

export function DocumentationTab({
  templateId,
  description,
  documentation,
  documentationUrl,
  setupGuide,
  changelog,
  faq,
  knownLimitations,
  environmentRequirements,
  onChange,
  isDarkMode = false,
}: DocumentationTabProps) {
  // Theme-aware colors
  const colors = isDarkMode ? {
    sectionTitle: 'text-white',
    sectionDescription: 'text-gray-400',
    inputBg: 'bg-gray-800',
    inputBorder: 'border-gray-600',
    inputText: 'text-white',
    inputPlaceholder: 'placeholder-gray-500',
    cardBg: 'bg-gray-800',
    cardBorder: 'border-gray-700',
    cardTitle: 'text-white',
    buttonText: 'text-blue-400 hover:text-blue-300',
    buttonDanger: 'text-red-400 hover:text-red-300',
    dashedBorder: 'border-gray-600 hover:border-gray-500',
    dashedText: 'text-gray-400 hover:text-gray-300',
    labelText: 'text-gray-300',
    bulletColor: 'text-gray-500',
  } : {
    sectionTitle: 'text-gray-900',
    sectionDescription: 'text-gray-500',
    inputBg: 'bg-white',
    inputBorder: 'border-gray-300',
    inputText: 'text-gray-900',
    inputPlaceholder: 'placeholder-gray-400',
    cardBg: 'bg-white',
    cardBorder: 'border-gray-200',
    cardTitle: 'text-gray-900',
    buttonText: 'text-blue-600 hover:text-blue-800',
    buttonDanger: 'text-red-500 hover:text-red-700',
    dashedBorder: 'border-gray-300 hover:border-gray-400',
    dashedText: 'text-gray-500 hover:text-gray-600',
    labelText: 'text-gray-700',
    bulletColor: 'text-gray-400',
  };
  // Changelog handlers
  const addChangelogEntry = () => {
    const newEntry: ChangelogEntry = {
      version: '',
      date: new Date().toISOString().split('T')[0],
      changes: [''],
    };
    onChange('changelog', [...changelog, newEntry]);
  };

  const updateChangelogEntry = (index: number, field: keyof ChangelogEntry, value: any) => {
    const updated = [...changelog];
    updated[index] = { ...updated[index], [field]: value };
    onChange('changelog', updated);
  };

  const removeChangelogEntry = (index: number) => {
    onChange('changelog', changelog.filter((_, i) => i !== index));
  };

  const addChangelogChange = (entryIndex: number) => {
    const updated = [...changelog];
    updated[entryIndex].changes.push('');
    onChange('changelog', updated);
  };

  const updateChangelogChange = (entryIndex: number, changeIndex: number, value: string) => {
    const updated = [...changelog];
    updated[entryIndex].changes[changeIndex] = value;
    onChange('changelog', updated);
  };

  const removeChangelogChange = (entryIndex: number, changeIndex: number) => {
    const updated = [...changelog];
    updated[entryIndex].changes = updated[entryIndex].changes.filter((_, i) => i !== changeIndex);
    onChange('changelog', updated);
  };

  // FAQ handlers
  const addFaqEntry = () => {
    onChange('faq', [...faq, { question: '', answer: '' }]);
  };

  const updateFaqEntry = (index: number, field: keyof FaqEntry, value: string) => {
    const updated = [...faq];
    updated[index] = { ...updated[index], [field]: value };
    onChange('faq', updated);
  };

  const removeFaqEntry = (index: number) => {
    onChange('faq', faq.filter((_, i) => i !== index));
  };

  // Known Limitations handlers
  const addLimitation = () => {
    onChange('knownLimitations', [...knownLimitations, '']);
  };

  const updateLimitation = (index: number, value: string) => {
    const updated = [...knownLimitations];
    updated[index] = value;
    onChange('knownLimitations', updated);
  };

  const removeLimitation = (index: number) => {
    onChange('knownLimitations', knownLimitations.filter((_, i) => i !== index));
  };

  // Environment Requirements handlers
  const addEnvironmentRequirement = (env: 'sandbox' | 'production') => {
    const existing = environmentRequirements.find(r => r.environment === env);
    if (existing) return;
    
    onChange('environmentRequirements', [
      ...environmentRequirements,
      { environment: env, requirements: [''], notes: '' },
    ]);
  };

  const updateEnvironmentRequirement = (
    index: number,
    field: keyof EnvironmentRequirement,
    value: any
  ) => {
    const updated = [...environmentRequirements];
    updated[index] = { ...updated[index], [field]: value };
    onChange('environmentRequirements', updated);
  };

  const addRequirementItem = (envIndex: number) => {
    const updated = [...environmentRequirements];
    updated[envIndex].requirements.push('');
    onChange('environmentRequirements', updated);
  };

  const updateRequirementItem = (envIndex: number, reqIndex: number, value: string) => {
    const updated = [...environmentRequirements];
    updated[envIndex].requirements[reqIndex] = value;
    onChange('environmentRequirements', updated);
  };

  const removeRequirementItem = (envIndex: number, reqIndex: number) => {
    const updated = [...environmentRequirements];
    updated[envIndex].requirements = updated[envIndex].requirements.filter((_, i) => i !== reqIndex);
    onChange('environmentRequirements', updated);
  };

  return (
    <div className="space-y-8">
      {/* Description */}
      <section>
        <h3 className={`text-lg font-medium ${colors.sectionTitle} mb-3 flex items-center gap-2`}>
          Descrição
        </h3>
        <Input
          value={description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="Descrição curta do template (exibida na listagem)"
          className={`w-full ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
        />
        <p className={`text-xs ${colors.sectionDescription} mt-1`}>
          Uma descrição breve que aparece nos cards da listagem de templates.
        </p>
      </section>

      {/* Documentation URL */}
      <section>
        <h3 className={`text-lg font-medium ${colors.sectionTitle} mb-3 flex items-center gap-2`}>
          <ExternalLink className="w-5 h-5" />
          Documentação Oficial
        </h3>
        <Input
          type="url"
          value={documentationUrl}
          onChange={(e) => onChange('documentationUrl', e.target.value)}
          placeholder="https://docs.provider.com"
          className={`w-full ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
        />
        <p className={`text-xs ${colors.sectionDescription} mt-1`}>
          Link para a documentação oficial do provider.
        </p>
      </section>

      {/* Main Documentation */}
      <section>
        <h3 className={`text-lg font-medium ${colors.sectionTitle} mb-3`}>
          Documentação Detalhada
        </h3>
        <MarkdownEditor
          value={documentation}
          onChange={(value) => onChange('documentation', value)}
          placeholder="Documentação detalhada do template em markdown..."
          minHeight={250}
          autoSaveKey={templateId ? `doc-${templateId}` : undefined}
          description="Documentação completa sobre o template, incluindo visão geral, recursos e limitações."
          isDarkMode={isDarkMode}
        />
      </section>

      {/* Setup Guide */}
      <section>
        <h3 className={`text-lg font-medium ${colors.sectionTitle} mb-3 flex items-center gap-2`}>
          <Settings className="w-5 h-5" />
          Guia de Configuração
        </h3>
        <MarkdownEditor
          value={setupGuide}
          onChange={(value) => onChange('setupGuide', value)}
          placeholder="Guia passo-a-passo de configuração..."
          minHeight={200}
          autoSaveKey={templateId ? `setup-${templateId}` : undefined}
          description="Instruções passo-a-passo para configurar o provider."
          isDarkMode={isDarkMode}
        />
      </section>

      {/* Environment Requirements */}
      <section>
        <h3 className={`text-lg font-medium ${colors.sectionTitle} mb-3 flex items-center gap-2`}>
          <Settings className="w-5 h-5" />
          Requisitos por Ambiente
        </h3>
        
        <div className="space-y-4">
          {['sandbox', 'production'].map((env) => {
            const envReq = environmentRequirements.find(r => r.environment === env);
            const envIndex = environmentRequirements.findIndex(r => r.environment === env);

            if (!envReq) {
              return (
                <button
                  key={env}
                  type="button"
                  onClick={() => addEnvironmentRequirement(env as 'sandbox' | 'production')}
                  className={`w-full p-4 border-2 border-dashed ${colors.dashedBorder} rounded-lg ${colors.dashedText} transition-colors`}
                >
                  + Adicionar requisitos para {env === 'sandbox' ? 'Sandbox' : 'Produção'}
                </button>
              );
            }

            return (
              <div key={env} className={`border ${colors.cardBorder} rounded-lg p-4 ${colors.cardBg}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`font-medium ${colors.cardTitle} capitalize`}>
                    {env === 'sandbox' ? '🧪 Sandbox' : '🚀 Produção'}
                  </h4>
                </div>

                <div className="space-y-2 mb-3">
                  {envReq.requirements.map((req, reqIndex) => (
                    <div key={reqIndex} className="flex items-center gap-2">
                      <Input
                        value={req}
                        onChange={(e) => updateRequirementItem(envIndex, reqIndex, e.target.value)}
                        placeholder="Requisito..."
                        className={`flex-1 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeRequirementItem(envIndex, reqIndex)}
                        className={`p-2 ${colors.buttonDanger}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => addRequirementItem(envIndex)}
                  className={`text-sm ${colors.buttonText}`}
                >
                  + Adicionar requisito
                </button>

                <div className="mt-3">
                  <Input
                    value={envReq.notes || ''}
                    onChange={(e) => updateEnvironmentRequirement(envIndex, 'notes', e.target.value)}
                    placeholder="Notas adicionais..."
                    className={`w-full ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Known Limitations */}
      <section>
        <h3 className={`text-lg font-medium ${colors.sectionTitle} mb-3 flex items-center gap-2`}>
          <AlertTriangle className="w-5 h-5 text-yellow-500" />
          Limitações Conhecidas
        </h3>
        
        <div className="space-y-2">
          {knownLimitations.map((limitation, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={limitation}
                onChange={(e) => updateLimitation(index, e.target.value)}
                placeholder="Descreva uma limitação..."
                className={`flex-1 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
              />
              <button
                type="button"
                onClick={() => removeLimitation(index)}
                className={`p-2 ${colors.buttonDanger}`}
              >
                ×
              </button>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addLimitation}
            className={`text-sm ${colors.buttonText}`}
          >
            + Adicionar limitação
          </button>
        </div>
      </section>

      {/* Changelog */}
      <section>
        <h3 className={`text-lg font-medium ${colors.sectionTitle} mb-3 flex items-center gap-2`}>
          <History className="w-5 h-5" />
          Histórico de Versões
        </h3>
        
        <div className="space-y-4">
          {changelog.map((entry, entryIndex) => (
            <div key={entryIndex} className={`border ${colors.cardBorder} rounded-lg p-4 ${colors.cardBg}`}>
              <div className="flex items-center gap-4 mb-3">
                <Input
                  value={entry.version}
                  onChange={(e) => updateChangelogEntry(entryIndex, 'version', e.target.value)}
                  placeholder="1.0.0"
                  className={`w-32 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
                />
                <Input
                  type="date"
                  value={entry.date}
                  onChange={(e) => updateChangelogEntry(entryIndex, 'date', e.target.value)}
                  className={`w-40 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
                />
                <button
                  type="button"
                  onClick={() => removeChangelogEntry(entryIndex)}
                  className={`ml-auto p-2 ${colors.buttonDanger}`}
                >
                  Remover
                </button>
              </div>

              <div className="space-y-2">
                {entry.changes.map((change, changeIndex) => (
                  <div key={changeIndex} className="flex items-center gap-2">
                    <span className={colors.bulletColor}>•</span>
                    <Input
                      value={change}
                      onChange={(e) => updateChangelogChange(entryIndex, changeIndex, e.target.value)}
                      placeholder="Descreva a mudança..."
                      className={`flex-1 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeChangelogChange(entryIndex, changeIndex)}
                      className={`p-1 ${colors.buttonDanger}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={() => addChangelogChange(entryIndex)}
                  className={`text-sm ${colors.buttonText} ml-4`}
                >
                  + Adicionar mudança
                </button>
              </div>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addChangelogEntry}
            className={`w-full p-3 border-2 border-dashed ${colors.dashedBorder} rounded-lg ${colors.dashedText} transition-colors`}
          >
            + Adicionar versão
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <h3 className={`text-lg font-medium ${colors.sectionTitle} mb-3 flex items-center gap-2`}>
          <HelpCircle className="w-5 h-5" />
          Perguntas Frequentes (FAQ)
        </h3>
        
        <div className="space-y-4">
          {faq.map((entry, index) => (
            <div key={index} className={`border ${colors.cardBorder} rounded-lg p-4 ${colors.cardBg}`}>
              <div className="flex items-start gap-2 mb-2">
                <span className="text-blue-500 font-medium">P:</span>
                <Input
                  value={entry.question}
                  onChange={(e) => updateFaqEntry(index, 'question', e.target.value)}
                  placeholder="Pergunta..."
                  className={`flex-1 ${colors.inputBg} ${colors.inputBorder} ${colors.inputText}`}
                />
                <button
                  type="button"
                  onClick={() => removeFaqEntry(index)}
                  className={`p-1 ${colors.buttonDanger}`}
                >
                  ×
                </button>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-500 font-medium">R:</span>
                <textarea
                  value={entry.answer}
                  onChange={(e) => updateFaqEntry(index, 'answer', e.target.value)}
                  placeholder="Resposta (suporta markdown)..."
                  className={`flex-1 px-3 py-2 border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]`}
                />
              </div>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addFaqEntry}
            className={`w-full p-3 border-2 border-dashed ${colors.dashedBorder} rounded-lg ${colors.dashedText} transition-colors`}
          >
            + Adicionar pergunta
          </button>
        </div>
      </section>
    </div>
  );
}

export default DocumentationTab;
